import { useState } from 'react';
import { Star, Loader2 } from 'lucide-react';
import { GlassModal } from '@/components/ui/glass-modal';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/lib/toast';
import { useLeaveReview } from '@/hooks/useTransactions';
import { REVIEW_TAGS, type ReviewTagId } from '@/hooks/useUserReviews';
import { goldGradientActive } from '@/lib/gradientStyles';
import { cn } from '@/lib/utils';
import { pillShape } from '@/lib/shapeStyles';
import { containsProfanity } from '@/lib/profanityFilter';

interface LeaveReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transactionId: string;
  reviewedUserId: string;
  reviewedUserName: string;
}

export function LeaveReviewDialog({
  open,
  onOpenChange,
  transactionId,
  reviewedUserId,
  reviewedUserName,
}: LeaveReviewDialogProps) {
  const leaveReview = useLeaveReview();
  const [rating, setRating] = useState(5);
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const [selectedTags, setSelectedTags] = useState<ReviewTagId[]>([]);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleTag = (tagId: ReviewTagId) => {
    setSelectedTags(prev =>
      prev.includes(tagId)
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  const handleSubmit = async () => {
    if (rating < 1 || rating > 5) {
      toast.error('Please select a rating');
      return;
    }

    if (comment.trim() && containsProfanity(comment)) {
      toast.error('Please use appropriate language in your comment');
      return;
    }

    setIsSubmitting(true);
    try {
      await leaveReview.mutateAsync({
        transactionId,
        reviewedUserId,
        rating,
        comment: comment.trim() || undefined,
        tags: selectedTags,
      });

      toast.success('Review submitted!');
      onOpenChange(false);
      setRating(5);
      setSelectedTags([]);
      setComment('');
    } catch (error) {
      console.error('Error leaving review:', error);
      toast.error('Failed to submit review');
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayRating = hoveredRating ?? rating;

  const body = (
    <>
      <div className="space-y-6 py-4">
        {/* Star Rating */}
        <div className="space-y-2">
          <Label>Rating</Label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(null)}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star
                  className={cn(
                    'w-8 h-8 transition-colors',
                    star <= displayRating
                      ? 'fill-active-gold text-active-gold'
                      : 'text-muted-foreground'
                  )}
                />
              </button>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            {displayRating === 1 && 'Poor'}
            {displayRating === 2 && 'Below Average'}
            {displayRating === 3 && 'Average'}
            {displayRating === 4 && 'Good'}
            {displayRating === 5 && 'Excellent'}
          </p>
        </div>

        {/* Tags */}
        <div className="space-y-2">
          <Label>What went well? (optional)</Label>
          <div className="flex flex-wrap gap-2">
            {REVIEW_TAGS.map(tag => (
              <Badge
                key={tag.id}
                variant={selectedTags.includes(tag.id) ? 'default' : 'outline'}
                className={cn(
                  pillShape,
                  'cursor-pointer transition-all',
                  selectedTags.includes(tag.id)
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-primary/10'
                )}
                onClick={() => toggleTag(tag.id)}
              >
                <span className="mr-1">{tag.emoji}</span>
                {tag.label}
              </Badge>
            ))}
          </div>
        </div>

        {/* Comment */}
        <div className="space-y-2">
          <Label htmlFor="comment">Comment (optional)</Label>
          <Textarea
            id="comment"
            placeholder="Share your experience..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground text-right">
            {comment.length}/500
          </p>
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={cn("flex-1", goldGradientActive)}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit Review'
          )}
        </Button>
      </div>
    </>
  );

  return (
    <GlassModal
      open={open}
      onOpenChange={onOpenChange}
      title="Leave a Review"
      description={`How was your experience with ${reviewedUserName}?`}
    >
      <div className="space-y-1 text-left">
        <h2 className="text-lg font-semibold text-foreground">Leave a Review</h2>
        <p className="text-sm text-muted-foreground">
          How was your experience with {reviewedUserName}?
        </p>
      </div>
      {body}
    </GlassModal>
  );
}
